/**
 * @component ry-icon
 * @category  display
 * @summary   Renders an icon from the icon registry by name (Fomantic UI `.icon` equivalent). One canonical name vocabulary (Fomantic), two visual sets — `fomantic` (default) and `lucide`. Live attribute updates re-render. Set `label` for screen-reader-visible icons; otherwise the icon is announced as decorative (`aria-hidden="true"`).
 *
 * Reference: Fomantic UI's icon element. ry-ui's icon system is registry-
 * driven — names resolve through `getIcon(name)` from `src/ts/core/ry-icons.ts`.
 * The default entry pre-imports `icons/fomantic.js`; opt into Lucide-style
 * glyphs with a side-effect import of `icons/lucide.js` plus
 * `setIconSet('lucide')`. There is NO cross-set fallback — names that aren't
 * registered in the active set render nothing. Consumers extend either set
 * via `registerIcon(name, svg)` (both sets) or `registerSet(set, icons)`
 * (one set only).
 *
 * @attribute name    string?              Icon name from the registry (e.g. `"close"`, `"check"`, `"chevron-down"`). Unknown names render nothing — the element is empty. Required for visible output.
 * @attribute size    string?   default="24"   Pixel size of the icon. Applied as width/height on the rendered SVG.
 * @attribute label   string?              Accessible label. When set, the inner SVG/i gets `role="img"` and `aria-label`. When absent, the inner element gets `aria-hidden="true"` (decorative).
 *
 * @property  name    string    Reflects the `name` attribute.
 * @property  size    number    Numeric (parseInt) view of the `size` attribute. Default 24.
 *
 * @method    rerender()   Force a re-render. Called by `setIconSet()` after a global icon-set switch — consumers rarely need to call directly.
 *
 * @content   None — `<ry-icon>` is configured entirely via attributes. innerHTML is replaced at every render.
 *
 * @dom
 *   ry-icon[name][size?][label?]
 *     > svg width=size height=size aria-label=label? aria-hidden="true"?
 *
 * @gotcha  Unknown icon names silently render nothing — no warning, no placeholder. Verify the name against the registry (`getIcon()` returns falsy for unknown). This is intentional to allow runtime swapping where some icons may not exist in every set.
 *
 * @gotcha  Live attribute updates re-render the SVG/i. The element is in `observedAttributes` for name, size, and label, so toggling any of them triggers a fresh `#render()`. Other components that wrap ry-icon should expect the inner SVG identity to change across renders.
 *
 * @gotcha  Without a `label` attribute, the icon is marked `aria-hidden="true"` — screen readers skip it entirely. This is correct for decorative icons next to text but WRONG for standalone interactive icons (e.g. icon-only buttons). For an icon-only button, set `label` OR put the `aria-label` on the parent control instead.
 *
 * @gotcha  The `name` property setter sets the attribute, which triggers `attributeChangedCallback` → re-render. Setting `el.name = 'x'` is equivalent to `el.setAttribute('name', 'x')`. Same for `size`.
 *
 * @example basic
 * ```html
 * <ry-icon name="close"></ry-icon>
 * ```
 *
 * @example sized
 * ```html
 * <ry-icon name="check" size="16"></ry-icon>
 * <ry-icon name="check" size="32"></ry-icon>
 * <ry-icon name="check" size="48"></ry-icon>
 * ```
 *
 * @example with-label
 * ```html
 * <!-- Standalone icon that screen readers should announce -->
 * <ry-icon name="warning" label="Warning"></ry-icon>
 * ```
 *
 * @example decorative-next-to-text
 * ```html
 * <!-- Icon is decorative; the surrounding text carries meaning -->
 * <button><ry-icon name="trash"></ry-icon> Delete</button>
 * ```
 *
 * @example in-button
 * ```html
 * <ry-button icon aria-label="Settings"><ry-icon name="settings"></ry-icon></ry-button>
 * ```
 *
 * @tutorial mount-renders-svg
 * ```js
 * const el = mount('<ry-icon name="close"></ry-icon>');
 * const svg = el.querySelector('svg');
 * await expectEqual(!!svg, true, 'inner svg rendered');
 * ```
 *
 * @tutorial unknown-name-renders-empty
 * ```js
 * // Wrong assumption: an invalid name renders a placeholder or warning.
 * // It does NOT — the element renders no inner content.
 * const el = mount('<ry-icon name="this-icon-does-not-exist-zzz"></ry-icon>');
 * await expectEqual(el.querySelector('svg'), null, 'no inner element for unknown name');
 * await expectEqual(el.innerHTML.trim(), '', 'innerHTML is empty');
 * ```
 *
 * @tutorial size-applied-to-svg
 * ```js
 * const el = mount('<ry-icon name="close" size="48"></ry-icon>');
 * const svg = el.querySelector('svg');
 * await expectAttr(svg, 'width', '48');
 * await expectAttr(svg, 'height', '48');
 * ```
 *
 * @tutorial label-vs-aria-hidden
 * ```js
 * // Wrong assumption: every icon is announced. Decorative icons (no label)
 * // get aria-hidden="true"; labeled icons get role="img" + aria-label.
 * const decorative = mount('<ry-icon name="close"></ry-icon>');
 * const svg1 = decorative.querySelector('svg');
 * await expectAttr(svg1, 'aria-hidden', 'true');
 *
 * const labeled = mount('<ry-icon name="close" label="Close dialog"></ry-icon>');
 * const svg2 = labeled.querySelector('svg');
 * await expectAttr(svg2, 'role', 'img');
 * await expectAttr(svg2, 'aria-label', 'Close dialog');
 * ```
 *
 * @tutorial live-attribute-update-rerenders
 * ```js
 * // Wrong assumption: attributes set at mount-time are baked. They are
 * // observed and trigger re-render on change.
 * const el = mount('<ry-icon name="close" size="16"></ry-icon>');
 * el.setAttribute('size', '64');
 * const svg = el.querySelector('svg');
 * await expectAttr(svg, 'width', '64', 'size update reflected on next render');
 * el.setAttribute('name', 'check');
 * // Inner element may have been replaced; query again
 * await expectEqual(!!el.querySelector('svg'), true, 'new icon rendered after name change');
 * ```
 *
 * @tutorial name-and-size-property-accessors
 * ```js
 * // .name and .size act as live property accessors over the attributes.
 * const el = mount('<ry-icon name="close" size="32"></ry-icon>');
 * await expectEqual(el.name, 'close');
 * await expectEqual(el.size, 32);
 * el.name = 'check';
 * await expectEqual(el.getAttribute('name'), 'check', 'property setter writes attribute');
 * el.size = 48;
 * await expectEqual(el.getAttribute('size'), '48');
 * ```
 *
 * @see ry-button
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export class RyIcon extends RyElement {
  static get observedAttributes(): string[] {
    return ['name', 'size', 'label'];
  }

  setup(): void {
    this.#render();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue !== newValue && this.hasAttribute('data-ry-initialized')) {
      this.#render();
    }
  }

  #render(): void {
    const name = this.getAttribute('name') ?? '';
    const size = this.getAttribute('size') ?? '24';
    const label = this.getAttribute('label');

    const html = getIcon(name);

    if (!html) {
      this.innerHTML = '';
      return;
    }

    this.innerHTML = html;

    const svgEl = this.querySelector('svg');
    if (!svgEl) return;
    svgEl.setAttribute('width', size);
    svgEl.setAttribute('height', size);
    if (label) {
      svgEl.setAttribute('role', 'img');
      svgEl.setAttribute('aria-label', label);
    } else {
      svgEl.setAttribute('aria-hidden', 'true');
    }
  }

  /** Called by setIconSet() to refresh after a global set switch. */
  rerender(): void {
    this.#render();
  }

  get name(): string {
    return this.getAttribute('name') ?? '';
  }

  set name(value: string) {
    this.setAttribute('name', value);
  }

  get size(): number {
    return parseInt(this.getAttribute('size') ?? '24', 10);
  }

  set size(value: number) {
    this.setAttribute('size', String(value));
  }
}

customElements.define('ry-icon', RyIcon);
